import jsPDF from "jspdf";
import html2canvas from "html2canvas";

// ---- Emoji → image (Twemoji) --------------------------------------------------
// html2canvas does not reliably rasterize native color emojis (they come out as
// empty boxes), so we swap each emoji for a Twemoji PNG inside the cloned document.
const U200D = String.fromCharCode(0x200d);
const UFE0Fg = /\uFE0F/g;
const EMOJI_SEQ = /(?:\p{Extended_Pictographic}(?:\uFE0F|\u200D\p{Extended_Pictographic})*)+/gu;

function toCodePoint(str, sep = "-") {
  const r = [];
  let c = 0, p = 0, i = 0;
  while (i < str.length) {
    c = str.charCodeAt(i++);
    if (p) {
      r.push((0x10000 + ((p - 0xd800) << 10) + (c - 0xdc00)).toString(16));
      p = 0;
    } else if (0xd800 <= c && c <= 0xdbff) {
      p = c;
    } else {
      r.push(c.toString(16));
    }
  }
  return r.join(sep);
}

function emojiToCodepoint(emoji) {
  return toCodePoint(emoji.indexOf(U200D) < 0 ? emoji.replace(UFE0Fg, "") : emoji);
}

function twemojiUrl(emoji) {
  return `https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/72x72/${emojiToCodepoint(emoji)}.png`;
}

function replaceEmojisWithImages(root) {
  const doc = root.ownerDocument;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  const textNodes = [];
  let n;
  while ((n = walker.nextNode())) {
    EMOJI_SEQ.lastIndex = 0;
    if (n.nodeValue && EMOJI_SEQ.test(n.nodeValue)) textNodes.push(n);
  }
  textNodes.forEach((node) => {
    const text = node.nodeValue;
    const frag = doc.createDocumentFragment();
    let last = 0;
    EMOJI_SEQ.lastIndex = 0;
    text.replace(EMOJI_SEQ, (match, offset) => {
      if (offset > last) frag.appendChild(doc.createTextNode(text.slice(last, offset)));
      const img = doc.createElement("img");
      img.src = twemojiUrl(match);
      img.alt = match;
      img.crossOrigin = "anonymous";
      img.style.cssText = "height:1em;width:auto;display:inline-block;vertical-align:-0.15em;margin:0 0.03em;";
      img.onerror = function () {
        const t = doc.createTextNode(match);
        if (this.parentNode) this.parentNode.replaceChild(t, this);
      };
      frag.appendChild(img);
      last = offset + match.length;
      return match;
    });
    if (last < text.length) frag.appendChild(doc.createTextNode(text.slice(last)));
    node.parentNode.replaceChild(frag, node);
  });
}

// Preload the Twemoji images so they are in the browser cache before html2canvas renders.
async function preloadEmojiImages(el) {
  const found = new Set();
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
  let n;
  while ((n = walker.nextNode())) {
    EMOJI_SEQ.lastIndex = 0;
    const m = n.nodeValue && n.nodeValue.match(EMOJI_SEQ);
    if (m) m.forEach((seq) => (seq.match(EMOJI_SEQ) || [seq]).forEach(() => found.add(seq)));
  }
  await Promise.all(
    [...found].map(
      (emoji) =>
        new Promise((resolve) => {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = img.onerror = () => resolve();
          img.src = twemojiUrl(emoji);
        })
    )
  );
}

/**
 * Capture a DOM element as a multi-page A4 PDF.
 * Simple fixed-height page slicing. Emojis are rendered as Twemoji images.
 */
export async function exportElementAsPdf(el, filename, { background = "#FDF8F0" } = {}) {
  if (!el) throw new Error("No element to capture");

  document.body.classList.add("capturing-pdf");
  await new Promise((r) => setTimeout(r, 200));

  try {
    await preloadEmojiImages(el);

    const canvas = await html2canvas(el, {
      backgroundColor: background,
      useCORS: true,
      allowTaint: true,
      imageTimeout: 20000,
      scale: 2,
      windowWidth: el.scrollWidth,
      windowHeight: el.scrollHeight,
      scrollY: 0,
      onclone: (clonedDoc, clonedEl) => {
        try { replaceEmojisWithImages(clonedEl || clonedDoc.body); } catch { /* ignore */ }
      },
    });

    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();

    const pxPerMm = canvas.width / pageW;
    const pageHpx = Math.floor(pageH * pxPerMm);

    // Block-aware page breaks: instead of slicing at a blind fixed height (which cut titles,
    // countdowns and cards in half and looked messy), snap each page's bottom edge to the gap
    // BETWEEN top-level invitation blocks so nothing gets split across pages.
    const ratio = canvas.height / (el.scrollHeight || canvas.height / pxPerMm);
    let breakpoints = [];
    try {
      const elTop = el.getBoundingClientRect().top;
      el.querySelectorAll('[data-testid^="block-"]').forEach((b) => {
        const top = b.getBoundingClientRect().top - elTop; // CSS px from top of captured element
        if (top > 2) breakpoints.push(Math.round(top * ratio));
      });
    } catch { /* ignore — fall back to fixed slicing */ }
    breakpoints = [...new Set(breakpoints)].filter((y) => y > 0 && y < canvas.height).sort((a, b) => a - b);
    breakpoints.push(canvas.height);

    const nextBreak = (start) => {
      const idealEnd = start + pageHpx;
      let end = -1;
      for (const bp of breakpoints) {
        if (bp > start && bp <= idealEnd) end = bp;
        else if (bp > idealEnd) break;
      }
      // No block boundary fits on this page (a single block taller than a page) → hard cut.
      if (end === -1) end = Math.min(idealEnd, canvas.height);
      return end;
    };

    let start = 0;
    let first = true;
    while (start < canvas.height - 1) {
      const end = nextBreak(start);
      const sliceHpx = end - start;
      const slice = document.createElement("canvas");
      slice.width = canvas.width;
      slice.height = sliceHpx;
      const ctx = slice.getContext("2d");
      ctx.fillStyle = background;
      ctx.fillRect(0, 0, slice.width, slice.height);
      ctx.drawImage(canvas, 0, start, canvas.width, sliceHpx, 0, 0, canvas.width, sliceHpx);

      const imgData = slice.toDataURL("image/jpeg", 0.92);
      const sliceHmm = sliceHpx / pxPerMm;
      if (!first) pdf.addPage();
      // Paint the full page background first so short pages (ending early at a block boundary)
      // keep the cream backdrop instead of a white gap.
      pdf.setFillColor(background);
      pdf.rect(0, 0, pageW, pageH, "F");
      pdf.addImage(imgData, "JPEG", 0, 0, pageW, sliceHmm);
      first = false;
      start = end;
    }

    pdf.save(filename);
  } finally {
    document.body.classList.remove("capturing-pdf");
  }
}
